import { useState } from 'react';

import { MeetingPlanPreviewModal } from './MeetingPlanPreviewModal';

import type { MeetingTemplate } from '@/types/thematic-meetings';

import { useGetMeetingTemplateCatalog, useImportMeetingTemplate } from '@/api/thematic-meetings';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import {
    TemplateListModal,
    type TemplateListTile,
} from '@/pages/ManageTemplates/components/AmelioActiveTemplates/TemplateListModal';
import { showSuccessToast } from '@/plugins/Toast';
import { getTextByLanguage } from '@/utils/localization';

type MeetingPlanCatalogModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

/**
 * The Amelio catalogue of meeting plans, in the list the evaluation templates are imported from. A
 * tile opens the plan's preview, which imports it; the copy joins the company's plans on the
 * Templates page, beside the ones it already holds.
 */
export const MeetingPlanCatalogModal = ({ isOpen, onClose }: MeetingPlanCatalogModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { handleError } = useError();

    const { data: catalog, isLoading, isError } = useGetMeetingTemplateCatalog({ enabled: isOpen });
    const { mutateAsync: importTemplate, isPending: isImporting } = useImportMeetingTemplate();

    // Kept after the preview closes so its content stays in place while the dialog fades out.
    const [previewedTemplate, setPreviewedTemplate] = useState<MeetingTemplate | undefined>();
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const openPreview = (template: MeetingTemplate) => {
        setPreviewedTemplate(template);
        setIsPreviewOpen(true);
    };

    const confirmImport = async () => {
        if (!previewedTemplate) {
            return;
        }

        try {
            await importTemplate(previewedTemplate.id);

            showSuccessToast({
                message: t('templateManagement.template.message.importTemplateSuccess'),
            });

            setIsPreviewOpen(false);
            onClose();
        } catch (error) {
            handleError({ error });
        }
    };

    const catalogNotice = () => {
        if (isError) {
            return t('templateManagement.template.meetingPlans.catalog.error');
        }

        if (!catalog?.length) {
            return t('templateManagement.template.meetingPlans.catalog.empty');
        }

        return undefined;
    };

    const toTile = (template: MeetingTemplate): TemplateListTile => ({
        title: getTextByLanguage(template.title, language),
        description: getTextByLanguage(template.objective, language),
        countLabel: t('templateManagement.template.meetingPlans.catalog.agendaCount', {
            count: template.agendaItems.length,
        }),
    });

    return (
        <>
            {/* The preview is declared before the list it opens from, as on the evaluation sections:
                when both close together, it releases its page scroll lock first. */}
            <MeetingPlanPreviewModal
                template={previewedTemplate}
                isOpen={isPreviewOpen}
                isImporting={isImporting}
                onClose={() => setIsPreviewOpen(false)}
                onImport={() => void confirmImport()}
            />

            <TemplateListModal
                isOpen={isOpen}
                onClose={onClose}
                title={t('templateManagement.template.meetingPlans.catalog.title')}
                subtitle={t('templateManagement.template.meetingPlans.catalog.subtitle')}
                iconName={'messages-message-circle'}
                templates={catalog ?? []}
                toTile={toTile}
                isLoading={isLoading}
                notice={catalogNotice()}
                onPreview={openPreview}
                dataTestId={'meeting-plan-catalog-modal'}
            />
        </>
    );
};
